// src/components/ReadOnlyCalendar.tsx

import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import { EventClickArg, DateSelectArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import index from "@beeldit/core/services";
import { Session } from "../models/Session";
import esLocale from "@fullcalendar/core/locales/es";
import { Button, Card, Modal } from "react-bootstrap";
import { t } from "i18next";
import { Calendar } from "@phosphor-icons/react";
import useEditAction from "@beeldit/core/actions/edit/useEditAction";
import useRemoveAction from "@beeldit/core/actions/remove/useRemoveAction";
import useCreateAction from "@beeldit/core/actions/create/useCreateAction";
import useInitialize from "@beeldit/core/actions/create/useInitialize";
import { TenantAndCommerceContext } from "../../contexts/TenantAndCommerceContext";
import config from "../config";
import { parseSpanishDateTime } from "../../modules/beeldit/core/utils/spanishDateTime";

interface CalendarEvent {
  id: string;
  title: string;
  start: string; // ISO string
  end: string; // ISO string
  description: string;
  google_event_id?: string;
  google_event_url?: string; // URL to open the event in Google Calendar
  [key: string]: any;
}

// Helpers a nivel de fichero para formatear horas (usadas en el componente y en convertSessionToEvent)
const formatTime = (d: Date) => {
  if (!d) return "";
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const hour12 = ((hours + 11) % 12) + 1; // 1-12
  const meridiem = hours >= 12 ? "pm" : "am";
  if (minutes === 0) {
    return `${hour12}${meridiem}`;
  }
  const minStr = minutes.toString().padStart(2, "0");
  return `${hour12}:${minStr}${meridiem}`;
};

const formatEventTime = (start?: Date | null, end?: Date | null) => {
  if (!start) return "";
  const s = formatTime(start);
  if (!end) return s;
  if (start.getTime() === end.getTime()) return s;
  return `${s} - ${formatTime(end)} `;
};

// Mismo punto de corte que el layout móvil del panel (custom.scss)
const MOBILE_QUERY = "(max-width: 767.98px)";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);
  return isMobile;
};

const SLOT_MINUTES = 60;

const DESKTOP_VIEW = "timeGridWeek";
const MOBILE_VIEW = "timeGridDay";

const DESKTOP_TOOLBAR = {
  left: "prev,next today",
  center: "title",
  right: "dayGridMonth,timeGridWeek,timeGridDay",
};
const MOBILE_TOOLBAR = {
  left: "prev,next today",
  center: "title",
  right: "timeGridDay,timeGridThreeDay",
};

const CALENDAR_VIEWS = {
  timeGridThreeDay: {
    type: "timeGrid",
    duration: { days: 3 },
    buttonText: "3 días",
  },
};

const ReadOnlyCalendar: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);
  const calendarRef = useRef<any>(null);
  const [moveDate, setMoveDate] = useState<string>("");
  const isMobile = useIsMobile();

  // Cambiar de vista al girar el móvil o redimensionar la ventana
  useEffect(() => {
    const api = calendarRef.current?.getApi?.();
    if (api) api.changeView(isMobile ? MOBILE_VIEW : DESKTOP_VIEW);
  }, [isMobile]);

  // En móvil el calendario ocupa todo el alto que queda de pantalla hasta la barra inferior
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const [mobileHeight, setMobileHeight] = useState<number>();

  useLayoutEffect(() => {
    if (!isMobile) return;
    const updateHeight = () => {
      const container = calendarContainerRef.current;
      if (!container) return;
      const top = container.getBoundingClientRect().top + window.scrollY;
      const bottomNavHeight =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--bottom-nav-height"
          )
        ) || 0;
      const bottomGap = 16; // padding inferior de la card y un poco de aire
      setMobileHeight(
        Math.max(window.innerHeight - top - bottomNavHeight - bottomGap, 350)
      );
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [isMobile]);

  // Función para centrar el calendario en los eventos de la semana actual
  const centerCalendarOnWeekEvents = React.useCallback(() => {
    setTimeout(() => {
      const api = calendarRef.current?.getApi?.();
      if (!api) return;

      const viewStart = api.view.currentStart;
      const viewEnd = api.view.currentEnd;

      // Filtrar eventos que están en la vista actual
      const eventsInView = events
        .map((e: any) => ({ ...e, startDate: new Date(e.start) }))
        .filter((e: any) => e.startDate >= viewStart && e.startDate < viewEnd);

      if (eventsInView.length === 0) return;

      // Ordenar por fecha/hora de inicio
      eventsInView.sort(
        (a: any, b: any) => a.startDate.getTime() - b.startDate.getTime()
      );

      // Obtener el evento más temprano y calcular la hora objetivo
      const earliest = eventsInView[0].startDate;
      const targetHour = Math.max(5, earliest.getHours() - 2);
      const timeStr = `${String(targetHour).padStart(2, "0")}:00:00`;

      try {
        api.scrollToTime(timeStr);
      } catch (e) {
        console.warn("Error al hacer scroll a la hora:", e);
      }
    }, 150);
  }, [events]);

  const loadSessions = React.useCallback(() => {
    // solicitar relaciones necesarias: customer, therapist y tratamiento
    const relations = "relations=customer,therapist,treatment";
    index("sessions", relations).then((response: any) => {
      const eventList = response.data.elements.map(convertSessionToEvent);
      setEvents(eventList);
    });
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Centrar el calendario cuando cambian los eventos
  useEffect(() => {
    if (events.length > 0) {
      centerCalendarOnWeekEvents();
    }
  }, [events, centerCalendarOnWeekEvents]);

  // (centering logic done inline after loading events)

  const getSelectedPatientName = (ev: CalendarEvent | null) => {
    if (!ev) return "";
    const customer = (ev as any).customer;
    if (!customer) return "Sin paciente";
    return `${customer.name} ${customer.first_lastname ?? ""} ${
      customer.second_lastname ?? ""
    }`.trim();
  };

  const getSelectedTreatmentName = (ev: CalendarEvent | null) => {
    if (!ev) return "";
    // prefer a direct treatment relation; fallback to customer_treatment.treatment
    const treat =
      (ev as any).treatment ?? (ev as any).customer_treatment?.treatment;
    const treatName = treat?.name;
    return treatName ?? "Sin tratamiento";
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const event = clickInfo.event;

    const calendarEvent: CalendarEvent = {
      id: event.id,
      title: event.title,
      start: event.start?.toISOString() ?? "",
      end: event.end?.toISOString() ?? "",
      description: event.extendedProps.description ?? "Sin descripción",
      google_event_id: event.extendedProps.google_event_id,
      google_event_url: event.extendedProps.google_event_url,
    };
    // attach relation objects if present on extendedProps
    (calendarEvent as any).customer = event.extendedProps.customer;
    (calendarEvent as any).therapist = event.extendedProps.therapist;
    // prefer a treatment relation if provided by the backend; otherwise keep customer_treatment
    (calendarEvent as any).treatment =
      event.extendedProps.treatment ??
      event.extendedProps.customer_treatment?.treatment;
    (calendarEvent as any).customer_treatment =
      event.extendedProps.customer_treatment;

    setSelectedEvent(calendarEvent);
    setShowModal(true);
  };

  const { getFunction, actionTemplate: editActionTemplate } = useEditAction(
    config,
    () => {
      loadSessions();
    },
    "xl"
  );

  const { actionTemplate: createActionTemplate } = useCreateAction(
    config,
    () => {
      loadSessions();
    }
  );

  // Importar useInitialize directamente para pasar datos dinámicos
  const initialize = useInitialize();

  const openCreateSession = (start: Date) => {
    // Formatear como string ISO completo (el componente BeelditDateTimeInput espera este formato)
    const formattedDateTime = new Date(start).toISOString();

    // Inicializar el formulario con la fecha y hora seleccionada
    initialize({ start_date_time: formattedDateTime });
  };

  // Handler para cuando se hace clic en una franja horaria
  const handleDateSelect = (selectInfo: DateSelectArg) => {
    openCreateSession(selectInfo.start);
  };

  // En pantallas táctiles FullCalendar solo selecciona tras mantener pulsado,
  // así que un toque simple sobre un hueco libre también abre el alta de sesión
  const handleDateClick = (clickInfo: DateClickArg) => {
    if (!clickInfo.jsEvent.type.startsWith("touch")) return; // con ratón ya lo gestiona `select`

    const start = clickInfo.date;
    const end = clickInfo.allDay
      ? new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1)
      : new Date(start.getTime() + SLOT_MINUTES * 60000);

    // Igual que selectOverlap={false}: no se puede crear sobre una franja ocupada
    const occupied = clickInfo.view.calendar
      .getEvents()
      .some((ev) => ev.start && ev.start < end && (ev.end ?? ev.start) > start);
    if (occupied) return;

    openCreateSession(start);
  };

  const { prepareRemoveFunction, actionTemplate: removeActionTemplate } =
    useRemoveAction(config, () => {
      loadSessions();
    });

  const navigate = useNavigate();
  const { tenantId, commerceId } = useContext(TenantAndCommerceContext);

  return (
    <>
      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between ps-3 bg-white">
          <h2 className="card-header-title">
            <Calendar size={24} />
            <span>{t("sessions")}</span>
          </h2>
        </Card.Header>

        <Card.Body className="p-2 p-md-3">
          {/* estilos locales para aumentar la altura de las franjas horarias */}
          <style>{`.read-only-calendar .fc-timegrid-slot { min-height: 140px !important; } .read-only-calendar .fc-timegrid-slot > td, .read-only-calendar .fc-timegrid-slot > div { min-height: 140px !important; }`}</style>
          <div className="d-flex align-items-center mb-3">
            <label className="me-2 mb-0">Mover a fecha</label>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ width: 180 }}
              value={moveDate}
              onChange={(e) => {
                setMoveDate(e.target.value);
                if (e.target.value) {
                  const d = new Date(e.target.value + "T00:00:00");
                  const api = calendarRef.current?.getApi?.();
                  if (api) {
                    api.gotoDate(d);
                    api.changeView(isMobile ? MOBILE_VIEW : DESKTOP_VIEW);
                  }
                }
              }}
            />
          </div>
          <p className="d-md-none text-muted small mb-2">
            Toca un hueco libre para añadir una sesión.
          </p>
          <div className="read-only-calendar" ref={calendarContainerRef}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={isMobile ? MOBILE_VIEW : DESKTOP_VIEW}
              height={isMobile ? mobileHeight : undefined}
              expandRows={isMobile}
              locale={esLocale}
              headerToolbar={isMobile ? MOBILE_TOOLBAR : DESKTOP_TOOLBAR}
              views={CALENDAR_VIEWS}
              events={events}
              eventClick={handleEventClick}
              select={handleDateSelect}
              dateClick={handleDateClick}
              datesSet={centerCalendarOnWeekEvents}
              editable={false}
              selectable={true}
              selectMirror={true}
              selectOverlap={false}
              dayMaxEvents={true}
              allDaySlot={false}
              slotMinTime="05:00:00"
              slotDuration={{ minutes: SLOT_MINUTES }}
              dayHeaderContent={(arg) => (
                <span
                  style={{ color: arg.date.getDay() === 0 ? "red" : undefined }}
                >
                  {arg.text}
                </span>
              )}
              slotLabelContent={(arg) => <span>{formatTime(arg.date)}</span>}
              eventContent={(arg) => {
                const ev = arg.event;
                const start = ev.start ?? null;
                const end = ev.end ?? null;
                const timeText = formatEventTime(start, end);
                return (
                  <>
                    <div className="fc-event-time">{timeText}</div>
                    <div className="fc-event-title">{ev.title}</div>
                  </>
                );
              }}
            />
          </div>
        </Card.Body>
      </Card>

      {editActionTemplate}
      {createActionTemplate}

      {selectedEvent && (
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Datos sesion</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Mostrar Paciente, Tratamiento, Hora de la cita (día y hora de inicio) */}
            <p>
              <strong>Paciente:</strong> {getSelectedPatientName(selectedEvent)}
            </p>
            <p>
              <strong>Tratamiento:</strong>{" "}
              {getSelectedTreatmentName(selectedEvent)}
            </p>
            <p>
              <strong>Hora de la cita:</strong>{" "}
              {new Date(selectedEvent.start).toLocaleString("es-ES", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </Modal.Body>
          <Modal.Footer>
            {selectedEvent.google_event_url && (
              <Button
                variant="secondary"
                onClick={() => {
                  window.open(selectedEvent.google_event_url, "_blank");
                  setShowModal(false);
                }}
              >
                Abrir en Google Calendar
              </Button>
            )}
            <Button
              variant="danger"
              onClick={() => {
                prepareRemoveFunction(Number(selectedEvent.id));
              }}
            >
              Eliminar
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => {
                const customerId =
                  (selectedEvent as any).customer?.id ??
                  (selectedEvent as any).customer_id;
                if (tenantId && commerceId && customerId) {
                  navigate(
                    `/panel/${tenantId}/${commerceId}/customers/${customerId}?tab=past-sessions`
                  );
                } else if (customerId) {
                  // fallback: generic customers path
                  navigate(
                    `/panel/1/1/customers/${customerId}?tab=past-sessions`
                  );
                }
                setShowModal(false);
              }}
            >
              Historial paciente
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                getFunction(Number(selectedEvent.id));
                setShowModal(false);
              }}
            >
              Editar sesión
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {removeActionTemplate}
    </>
  );
};

export default ReadOnlyCalendar;

function convertSessionToEvent(session: Session): CalendarEvent {
  // Parsear la fecha UTC del backend como fecha local española
  const start = parseSpanishDateTime(session.start_date_time);

  // Usar la duración de la sesión directamente, ya que viene en minutos desde el backend
  const duration = session.duration || 60; // Solo usar 60 como fallback si es falsy (0, null, undefined)
  const end = new Date(start.getTime() + duration * 60000);

  const customer = session.customer;
  const customerName = customer
    ? `${customer.name} ${customer.first_lastname ?? ""} ${
        customer.second_lastname ?? ""
      }`.trim()
    : "Sin cliente";

  const therapist = (session as any).therapist;
  const customerTreatment = (session as any).customer_treatment;
  // prefer session.treatment if backend provides it directly
  const directTreatment = (session as any).treatment ?? null;

  // title: Paciente | hora | terapeuta
  const timeLabel = formatTime(start);
  const therapistName = therapist
    ? `${therapist.name} ${therapist.first_lastname ?? ""}`.trim()
    : "Sin terapeuta";

  // Determinar el color del evento basado en el estado y el terapeuta
  let backgroundColor = "#f8f9fa"; // Color por defecto (gris claro)
  let borderColor = "#dee2e6";

  // Verificar si la sesión está cancelada o marcada como no asistencia
  // Status típicos: 2 = cancelada, 3 = no asistencia (primera vez), 4 = no asistencia (reincidente)
  const isCancelled =
    session.cancelled ||
    session.no_show ||
    session.status === 2 || // Cancelada
    session.status === 3 || // No asistencia
    session.status === 4; // No asistencia reincidente

  if (!isCancelled && therapist && therapist.color) {
    // Usar el color del terapeuta si no está cancelada
    backgroundColor = therapist.color;
    borderColor = therapist.color;
  } else if (isCancelled) {
    // Sesión cancelada o no asistencia - gris
    backgroundColor = "#e9ecef";
    borderColor = "#adb5bd";
  }

  return {
    id: String(session.id),
    title: `${customerName} | ${timeLabel} | ${therapistName}`,
    start: start.toISOString(),
    end: end.toISOString(),
    description: session.internal_comment ?? "Sin observaciones",
    backgroundColor: backgroundColor,
    borderColor: borderColor,
    textColor: "#000000", // Texto negro para contraste
    google_event_id: session.google_event_id,
    google_event_url: session.google_event_url,
    // include relation objects explicitly in extendedProps so FullCalendar
    // exposes them under event.extendedProps
    extendedProps: {
      // @ts-ignore
      customer: customer,
      // @ts-ignore
      therapist: therapist,
      // @ts-ignore
      treatment: directTreatment ?? customerTreatment?.treatment,
      // @ts-ignore
      customer_treatment: customerTreatment,
    },
  } as any;
}
